import { useMemo, useCallback, useState, useEffect, useRef } from "react";
import { UserData } from "@/entities/users/types";
import { useUsersInfinite } from "@/entities/users/hooks";
import { FilterTab } from "@/components/ui/FilterTabs";
import { useTranslation } from "react-i18next";
import { SortingState } from "@tanstack/react-table";
import {
  StatusCounts,
  UserFilters,
  SearchParams,
  StatusAggregation,
  UserSelectionHook,
  UserManagementHook,
  STATUS_FILTER_MAP,
  STATUS_VALUE_MAP,
  TRANSLATION_KEY,
  DEBOUNCE_DELAY,
  PAGE_SIZE
} from "./types";

const useDebounce = (value: string, delay: number): string => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [value, delay]);

  return debouncedValue;
};

const useUserSelection = (users: UserData[] | undefined): UserSelectionHook => {
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);

  const handleUserSelect = useCallback((userId: string, checked: boolean) => {
    setSelectedUsers((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(userId);
      } else {
        newSet.delete(userId);
      }
      return newSet;
    });
  }, []);

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      setSelectAll(checked);
      if (checked) {
        setSelectedUsers(new Set(users?.map((user) => user.id) || []));
      } else {
        setSelectedUsers(new Set());
      }
    },
    [users]
  );

  const clearSelection = useCallback(() => {
    setSelectedUsers(new Set());
    setSelectAll(false);
  }, []);

  const selectedUserData = useMemo(() => {
    if (!users) return [];
    return users.filter((user) => selectedUsers.has(user.id));
  }, [users, selectedUsers]);

  // Update selectAll state based on current selection
  useEffect(() => {
    if (users && users.length > 0) {
      const allSelected = users.every((user) => selectedUsers.has(user.id));
      const someSelected = users.some((user) => selectedUsers.has(user.id));
      setSelectAll(allSelected && someSelected);
    }
  }, [selectedUsers, users]);

  return {
    selectedUsers,
    selectAll,
    selectedUserData,
    handleUserSelect,
    handleSelectAll,
    clearSelection
  };
};

const createFilters = (activeFilter: string): UserFilters => {
  if (activeFilter === "all") return {};

  const status = STATUS_FILTER_MAP[activeFilter];
  return status ? { status: [status] } : {};
};

const createSearchParams = (searchTerm: string): SearchParams | undefined => {
  return searchTerm.trim() ? { term: searchTerm.trim() } : undefined;
};

const processStatusCounts = (
  aggregations: StatusAggregation | undefined,
  totalCount: number
): StatusCounts => {
  const counts: StatusCounts = {
    all: totalCount,
    active: 0,
    inactive: 0,
    pending: 0,
    expired: 0
  };

  if (!aggregations?.data) return counts;

  aggregations.data.forEach((item) => {
    const statusKey = STATUS_VALUE_MAP[item.fieldValue];
    if (statusKey) {
      counts[statusKey] = item.fields[0]?.aggregations?.count || 0;
    }
  });

  return counts;
};

const createFilterTabs = (
  statusCounts: StatusCounts,
  t: (key: string) => string
): FilterTab[] => {
  const filterKeys = [...Object.keys(STATUS_FILTER_MAP), "all"] as Array<
    keyof StatusCounts
  >;
  return filterKeys.map((key) => ({
    key,
    label: t(`${TRANSLATION_KEY}.filters.${key}`),
    count: statusCounts[key]
  }));
};

// Stable reference for the aggregations query (no filters) — an inline {} would be
// a new object every render and churn the query hook.
const EMPTY_FILTERS: UserFilters = {};

export const useUserManagement = (): UserManagementHook => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("active");
  const [sorting, setSorting] = useState<SortingState>([
    { id: "displayName", desc: false }
  ]);
  const debouncedSearchTerm = useDebounce(searchTerm, DEBOUNCE_DELAY);
  const filters = useMemo(() => createFilters(activeFilter), [activeFilter]);
  const searchParams = useMemo(
    () => createSearchParams(debouncedSearchTerm),
    [debouncedSearchTerm]
  );

  const sortParams = useMemo(
    () =>
      sorting.length > 0
        ? {
            field: sorting[0].id,
            asc: !sorting[0].desc
          }
        : undefined,
    [sorting]
  );

  const {
    entities: users,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    isError,
    fetchNextPage
  } = useUsersInfinite(
    filters,
    searchParams,
    undefined,
    PAGE_SIZE,
    sortParams?.field,
    sortParams?.asc,
    2
  );

  // Aggregations for tab counts (always fetch all statuses)
  const { aggregations: statusAggregations, total: totalCount = 0 } =
    useUsersInfinite(
      EMPTY_FILTERS, // No filters for aggregations
      searchParams,
      "status",
      1,
      undefined,
      undefined,
      2
    );

  const {
    selectedUsers,
    selectAll,
    selectedUserData,
    handleUserSelect,
    handleSelectAll,
    clearSelection
  } = useUserSelection(users);

  const statusCounts = useMemo(
    () =>
      processStatusCounts(statusAggregations as StatusAggregation, totalCount),
    [statusAggregations, totalCount]
  );

  const filterTabs = useMemo(
    () => createFilterTabs(statusCounts, t),
    [statusCounts, t]
  );

  return {
    searchTerm,
    activeFilter,
    selectedUsers,
    selectAll,
    users,
    selectedUserData,
    filterTabs,
    statusCounts,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    isError,
    sorting,
    setSearchTerm,
    setActiveFilter,
    handleUserSelect,
    handleSelectAll,
    clearSelection,
    onLoadMore: fetchNextPage,
    onSortingChange: setSorting
  };
};
